import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReducedMotion } from "framer-motion";

export interface CarouselProps {
  /** One element per slide. Each is stretched to the tallest slide's height. */
  children: React.ReactNode;
  /** Accessible name for the scrollable region. */
  label: string;
  /** Gap between slides, in px. Must match the Tailwind gap below. */
  gap?: number;
  className?: string;
}

/** Tailwind has no arbitrary-value gap that JS can read back, so the one gap
 *  the track uses is declared here and applied as an inline style. */
const DEFAULT_GAP = 24;

/**
 * Horizontal scroll-snap carousel.
 *
 * Native overflow scrolling does the work -- no virtualization, no transform
 * bookkeeping -- so touch, trackpad, and keyboard all behave the way the OS
 * intends, and the arrows are a convenience layered on top rather than the
 * only way through. The arrows only appear when the track actually overflows,
 * so a strip that already fits is not decorated with dead controls.
 */
const Carousel: React.FC<CarouselProps> = ({
  children,
  label,
  gap = DEFAULT_GAP,
  className = "",
}) => {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const reduceMotion = useReducedMotion();

  const [overflowing, setOverflowing] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const measure = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    const max = scrollWidth - clientWidth;

    // The track's own padding shifts where scroll snapping comes to rest: with
    // `px-1` the first slide settles at scrollLeft 4, not 0. Measuring against
    // a bare 0 would leave Back enabled while already at the start, so the
    // resting offset is read off the element rather than assumed. The extra
    // pixel of slack absorbs fractional layout widths.
    const style = getComputedStyle(track);
    const startRest = parseFloat(style.paddingLeft) || 0;
    const endRest = parseFloat(style.paddingRight) || 0;

    setOverflowing(max > startRest + endRest + 1);
    setAtStart(scrollLeft <= startRest + 1);
    setAtEnd(scrollLeft >= max - endRest - 1);
  }, []);

  // Layout effect: the arrows must be right on the first paint, not one frame
  // after it, or they flash in as the strip renders.
  useLayoutEffect(measure);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || typeof ResizeObserver === "undefined") return;
    // Watches the track and its slides: a card that grows when its image loads
    // changes whether the strip overflows.
    const observer = new ResizeObserver(measure);
    observer.observe(track);
    Array.from(track.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [measure, children]);

  const scrollByOneSlide = (direction: 1 | -1) => {
    const track = trackRef.current;
    if (!track) return;
    const first = track.firstElementChild;
    // Stepping by one slide rather than one viewport keeps the arrows
    // predictable at every breakpoint, however many slides happen to fit.
    const step = first ? first.getBoundingClientRect().width + gap : track.clientWidth;
    track.scrollBy({
      left: direction * step,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  const arrowClass =
    "rounded-full border border-gray-700 bg-gray-900/80 p-2 text-gray-300 " +
    "transition-colors hover:border-gray-600 hover:text-white " +
    "disabled:pointer-events-none disabled:opacity-30";

  return (
    <div className={`relative ${className}`}>
      {overflowing && (
        <div className="mb-3 flex justify-end gap-2">
          <button
            type="button"
            aria-label={`Scroll ${label} backwards`}
            className={arrowClass}
            disabled={atStart}
            onClick={() => scrollByOneSlide(-1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            type="button"
            aria-label={`Scroll ${label} forwards`}
            className={arrowClass}
            disabled={atEnd}
            onClick={() => scrollByOneSlide(1)}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}

      <div
        ref={trackRef}
        onScroll={measure}
        // `group` + aria-roledescription is the APG carousel pattern. Not
        // `region`: that is a landmark, and the strip usually sits inside a
        // <section> of the same name, which would announce it twice.
        role="group"
        aria-roledescription="carousel"
        aria-label={label}
        // Focusable so keyboard users can page through with the arrow keys
        // without reaching for the buttons at all.
        tabIndex={0}
        // -mx/px pair lets slides bleed to the section's edge while the first
        // and last still align with the surrounding copy.
        className="-mx-1 flex snap-x snap-mandatory items-stretch overflow-x-auto scroll-smooth px-1 pb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        style={{ gap, scrollbarWidth: "thin" }}
      >
        {children}
      </div>
    </div>
  );
};

export default Carousel;
